import type { ClickReleaseConfig } from '../config/types';

/**
 * Compile the effective config's friendly color knobs (and banner colors) into the raw `--cr-*`
 * CSS custom properties the stylesheet consumes. Only knobs the client actually set emit a
 * property, so unset tokens keep their bundled default from `styles/base.ts`. The raw
 * `theme.vars` escape hatch is applied last, so it wins over any named color.
 */
export function cssVarsFromConfig(config: ClickReleaseConfig): Record<string, string> {
  const vars: Record<string, string> = {};
  const c = config.theme.colors ?? {};

  if (c.accent) vars['--cr-accent'] = c.accent;
  if (c.accentContrast) vars['--cr-accent-contrast'] = c.accentContrast;
  if (c.text) vars['--cr-fg'] = c.text;
  if (c.background) {
    vars['--cr-surface'] = c.background;
    vars['--cr-bg'] = c.background;
  }
  if (c.muted) vars['--cr-muted'] = c.muted;
  if (c.border) vars['--cr-border'] = c.border;
  if (c.dot) vars['--cr-dot'] = c.dot;
  if (c.overlay) vars['--cr-overlay'] = c.overlay;
  if (c.radius) {
    // One knob rounds the whole UI: the surface corner and the control (button) corner together.
    vars['--cr-radius'] = c.radius;
    vars['--cr-radius-control'] = c.radius;
  }
  if (c.font) vars['--cr-font'] = c.font;

  // Per-surface banner colors (the stylesheet falls back to the accent tokens when unset).
  if (config.banner.background) vars['--cr-banner-bg'] = config.banner.background;
  if (config.banner.color) vars['--cr-banner-fg'] = config.banner.color;

  // Raw escape hatch wins over the named knobs above.
  for (const [name, value] of Object.entries(config.theme.vars ?? {})) {
    if (name.startsWith('--')) vars[name] = value;
  }
  return vars;
}
